import { breadcrumbs, crumb, installErrorCapture } from "./errors";
import { DEFAULT_ATTRIBUTES, sectionInView } from "./identify";
import { Recorder } from "./recorder";
import { runSelfTest } from "./selftest";
import { mountWidget } from "./widget";

export type { Target } from "./identify";

export type Options = {
  /** Project public key (pk_…) from the dashboard. */
  key: string;
  /** Base URL of the feedback Worker, e.g. https://feedback.example.workers.dev */
  endpoint: string;
  /** Attributes that identify sections/components, checked in order. Nested ones form a tree. */
  attributes?: string[];
  /** Show the feedback button. Turn off to only capture errors. Default true. */
  widget?: boolean;
  /** Capture window errors and unhandled promise rejections. Default true. */
  errors?: boolean;
  /** Also capture console.error calls. Default true. */
  console?: boolean;
  /** Capture failed fetch/XHR calls (5xx and network errors). Default true. */
  network?: boolean;
  /** Build/version label attached to every report. */
  release?: string;
  /** Attach a screenshot of the visible page to each note. Default true. */
  screenshots?: boolean;
  /** Key that starts talk mode, and closes the widget when it is open. Default "f"; false to turn off. */
  hotkey?: string | false;
  /** Shown before the first voice recording. */
  consentText?: string;
};

let started = false;
let errorApi: ReturnType<typeof installErrorCapture> | undefined;

function storage(key: string, value?: string): string | null {
  try {
    if (value !== undefined) value ? localStorage.setItem(key, value) : localStorage.removeItem(key);
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function reporter(): string {
  const name = storage("afb:reporter-name");
  let anon = storage("afb:anon-id");
  if (!anon) anon = storage("afb:anon-id", "anon-" + Math.random().toString(36).slice(2, 8)) ?? "anon";
  return name ? `${name} (${anon})` : anon;
}

export function init(options: Options) {
  if (started || typeof window === "undefined") return;
  started = true;
  const endpoint = options.endpoint.replace(/\/$/, "");
  const attributes = options.attributes ?? DEFAULT_ATTRIBUTES;
  let host: Element | null = null;

  const context = () => ({
    viewport: `${innerWidth}x${innerHeight}`,
    screen: `${screen.width}x${screen.height}`,
    touchPoints: navigator.maxTouchPoints || 0,
    dpr: devicePixelRatio,
    userAgent: navigator.userAgent,
    language: navigator.language,
    release: options.release,
    sectionInView: host ? sectionInView(attributes, host) : undefined,
    scrollY: Math.round(scrollY),
    breadcrumbs: breadcrumbs.slice(-10),
  });

  // The key's capture mode, set in the dashboard: "all" shows the widget, "errors" captures errors
  // only, "off" does nothing. Errors are caught from the start and held until the answer arrives.
  // A server that predates the setting, or can't be reached, means "all", as before.
  const mode: Promise<"all" | "errors" | "off"> = fetch(`${endpoint}/v1/config?key=${encodeURIComponent(options.key)}`, { mode: "cors" })
    .then((r) => (r.ok ? r.json() : { capture: "all" }))
    .then((d: { capture?: string }) => (d.capture === "off" || d.capture === "errors" ? d.capture : "all"))
    .catch(() => "all" as const);

  if (options.errors !== false) {
    errorApi = installErrorCapture(
      { endpoint, key: options.key, attributes, console: options.console !== false, network: options.network !== false, context, reporter },
      (el) => !!host && el instanceof Node && host.contains(el),
    );
    void mode.then((m) => errorApi?.setEnabled(m !== "off"));
  }

  // Navigation breadcrumbs for single-page apps.
  for (const fn of ["pushState", "replaceState"] as const) {
    const original = history[fn];
    history[fn] = function (this: History, ...args: Parameters<History["pushState"]>) {
      const result = original.apply(this, args);
      crumb("nav", location.pathname + location.search);
      return result;
    };
  }
  addEventListener("popstate", () => crumb("nav", location.pathname + location.search));

  if (options.widget !== false && !Recorder.allowedByPolicy()) {
    console.warn(
      "[agentic-feedback] This page's Permissions-Policy blocks the microphone (microphone=()), so testers can't record voice notes. " +
        "Allow it where feedback is on, e.g. Permissions-Policy: microphone=(self).",
    );
  }

  if (options.widget !== false) {
    const mount = () => {
      host = mountWidget({
        endpoint,
        key: options.key,
        attributes,
        hotkey: options.hotkey === undefined ? "f" : options.hotkey,
        screenshots: options.screenshots !== false,
        consentText:
          options.consentText ??
          "Your voice is recorded only while recording is on, and sent to the team building this site so they can act on your feedback.",
        context,
        reporter,
        setReporter: (name) => storage("afb:reporter-name", name),
      }).host;
    };
    void mode.then((m) => {
      if (m !== "all") return;
      document.body ? mount() : addEventListener("DOMContentLoaded", mount);
    });
  }

  const nonce = new URLSearchParams(location.search).get("afb-selftest");
  if (nonce && /^[\w-]{4,64}$/.test(nonce)) {
    const run = () =>
      runSelfTest({
        endpoint,
        key: options.key,
        attributes,
        nonce,
        context,
        reporter,
        captureError: (source, message, stack) => errorApi?.capture(source, message, stack),
        flush: () => errorApi?.flush(),
        screenshots: options.widget !== false && options.screenshots !== false,
        exclude: host,
      });
    // Give the app a moment to render so element identification has something to look at.
    const start = () => (document.readyState === "complete" ? setTimeout(run, 1500) : addEventListener("load", () => setTimeout(run, 1500)));
    void mode.then((m) => (m === "off" ? console.warn("[agentic-feedback] self-test skipped: capture is off for this key in the dashboard") : start()));
  }
}

/** Report a caught error manually. */
export function captureError(error: unknown) {
  const e = error instanceof Error ? error : new Error(String(error));
  errorApi?.capture("manual", `${e.name}: ${e.message}`, e.stack);
}

/** Name the tester, e.g. from your own login. */
export function identify(name: string) {
  storage("afb:reporter-name", name);
}
